/**
 * BUG-002 helper — wrap a CallDispatcher.call to clear the Navbar
 * `lastError` badge on the next successful CALLRESULT.
 *
 * The store's connect() path uses this directly. Extracted into its own
 * module so the behaviour can be unit-tested without spinning up the
 * full station runtime.
 */

export interface CallReturn {
  readonly messageId: string;
  readonly response: Promise<Record<string, unknown>>;
}

export interface CallFn {
  (
    action: string,
    payload: Record<string, unknown>,
    timeoutMs?: number,
  ): CallReturn;
}

export interface ClearErrorOnSuccessHooks {
  readonly getLastError: () => string | null;
  readonly clearLastError: () => void;
}

/**
 * Return a new `call` that proxies to `inner` but, on every successful
 * CALLRESULT, runs `clearLastError()` if `getLastError()` is non-null.
 * CALLERROR / timeouts keep the error visible — they go through the
 * promise's catch path which this wrapper does not touch.
 */
export function wrapCallClearingErrorOnSuccess(
  inner: CallFn,
  hooks: ClearErrorOnSuccessHooks,
): CallFn {
  return (action, payload, timeoutMs) => {
    const sent = inner(action, payload, timeoutMs);
    const response = sent.response.then((p) => {
      if (hooks.getLastError() !== null) hooks.clearLastError();
      return p;
    });
    return { messageId: sent.messageId, response };
  };
}
